import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Outlet } from 'react-router-dom';
import { getSubdomain } from '../utils/subdomainHelper';
import apiClient from '../services/apiClient';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

// Lazy load templates
const Template1 = lazy(() => import('../templates/template1/StoreFront'));
const Template2 = lazy(() => import('../templates/template2/StoreFront'));

// Interface for shop data
interface Shop {
	id: number;
	name: string;
	description: string | null;
	hash_id: string;
	currency: string;
	subdomain: string;
	logo_url?: string;
	storefront_template?: string;
}

// Interface for price range
interface PriceRange {
	min: number;
	max: number;
}

// Interface for product data
interface Product {
	id: number;
	name: string;
	description: string;
	default_price: number;
	options_type?: 'variant' | 'addon';
	price_range: PriceRange | null;
	product_images?: string[] | string;
	category?: string;
	in_stock?: boolean;
	addon_groups?: any[]; // For addon products
}

// Interface for the shop and products data
interface StorefrontData {
	shop: Shop;
	products: Product[];
}

// Function to get the appropriate template component
const getTemplateComponent = (templateName?: string) => {
	switch (templateName) {
		case 'template2':
			return Template2;
		case 'template1':
		default:
			return Template1; // Default to template1 if not specified or unknown template
	}
};

const StorefrontRoute: React.FC = () => {
	const [loading, setLoading] = useState(true);
	const [storeData, setStoreData] = useState<StorefrontData | null>(null);
	const [notFound, setNotFound] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const subdomain = getSubdomain();

	useEffect(() => {
		if (!subdomain) {
			// No subdomain, not a storefront request
			setLoading(false);
			return;
		}

		// Fetch shop data by subdomain
		apiClient
			.get(`/storefront/${subdomain}`)
			.then((response) => {
				console.log('Response from API:', response);
				if (response.data && response.status === 'success') {
					setStoreData(response.data);
				} else {
					setError('Unable to load store data');
				}
				setLoading(false);
			})
			.catch((error) => {
				console.error('Error fetching shop data:', error);
				// Check for ApiClientError which has the response object
				if (error.response && error.response.status === 404) {
					setNotFound(true);
				} else {
					setError('Error loading store data. Please try again later.');
				}
				setLoading(false);
			});
	}, [subdomain]);

	if (loading) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '100vh',
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	if (!subdomain) {
		// No subdomain, should use normal app routes
		return <Outlet />;
	}

	if (notFound) {
		// Shop not found, render the NotFound component directly without navigation
		// This ensures we don't redirect to the main app's NotFound route which has the Navigation component
		return (
			<div className="storefront-not-found">
				<div
					style={{
						backgroundColor: '#f3f4f8',
						width: '100%',
						minHeight: '100vh',
						padding: '30px 40px',
						boxSizing: 'border-box',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<h1 style={{ color: 'grey', margin: '0px', fontSize: '100px' }}>404</h1>
					<h1 style={{ color: 'grey' }}>Shop Not Found</h1>
					<a
						href={process.env.REACT_APP_WEBSITE_URL || 'https://vendready.store'}
						style={{ color: 'grey', textDecoration: 'none' }}
					>
						Return to VendReady
					</a>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '100vh',
					padding: '20px',
					textAlign: 'center',
				}}
			>
				<h2>Something went wrong</h2>
				<p>{error}</p>
				<button
					onClick={() => window.location.reload()}
					style={{
						marginTop: '20px',
						padding: '10px 20px',
						backgroundColor: '#4285F4',
						color: 'white',
						border: 'none',
						borderRadius: '4px',
						cursor: 'pointer',
					}}
				>
					Try Again
				</button>
			</Box>
		);
	}

	// Get the appropriate template component based on shop's template setting
	const TemplateComponent = getTemplateComponent(storeData?.shop?.storefront_template);

	// Render the storefront with shop data using the selected template
	return (
		<Suspense
			fallback={
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						minHeight: '100vh',
					}}
				>
					<CircularProgress />
				</Box>
			}
		>
			<TemplateComponent shopData={storeData} />
		</Suspense>
	);
};

export default StorefrontRoute;
